import api from '../../services/api'
import type { BruktbilFilters } from './useBruktbilFilters'

// Shared client for the Sammenligning tabs on Selgere (#484) and Kjøpere
// (#488). Both sides hit the same side-parameterised backend shape, so the
// only thing that differs is the path segment.

/** Max orgs that can be compared at once — mirrored server-side. */
export const MAX_SAMMENLIGNING_ORGS = 10

export type OrgSide = 'selger' | 'kjoper'

const PATH: Record<OrgSide, string> = { selger: 'selgere', kjoper: 'kjopere' }

export interface SammenligningPunkt {
  maned: string // "YYYY-MM"
  antall: number
}

/** One buyer class and this org's share of its own volume in it (#594 AC5). */
export interface KjoperKlasseAndel {
  /** The BUYER half of class_ — the raw token, e.g. PERSON / FIRMA / BILUTLEIE. */
  klasse: string
  antall: number
  /** Share of the org's own total for the period, 0–100. */
  andel: number
}

export interface OrgSammenligningSerie {
  org_nr: string
  org_navn: string | null
  total: number
  trend: SammenligningPunkt[]
  avg_bilalder: number | null
  /** Private buyers only; null (not 0) when the org had none. */
  avg_kjoperalder: number | null
  /** Ranked by count desc; fewer than three entries when the org has fewer. */
  kjoper_klasser: KjoperKlasseAndel[]
}

export interface OrgSammenligningResponse {
  maneder: string[]
  serier: OrgSammenligningSerie[]
}

export interface OrgOption {
  orgNr: string
  navn: string | null
}

type SammenligningParams = Pick<BruktbilFilters, 'from' | 'to' | 'klasse' | 'varebil' | 'omregistrering'> & {
  selgerPostnummer?: string | null
  kjoperPostnummer?: string | null
}

function baseParams(f: SammenligningParams) {
  return {
    from_date: f.from,
    to_date: f.to,
    klasse: f.klasse,
    varebil: f.varebil,
    omregistrering: f.omregistrering || undefined,
    ...(f.selgerPostnummer ? { selger_postnummer: f.selgerPostnummer.split(',') } : {}),
    ...(f.kjoperPostnummer ? { kjoper_postnummer: f.kjoperPostnummer.split(',') } : {}),
  }
}

export function fetchOrgSammenligning(
  side: OrgSide,
  params: SammenligningParams,
  orgNrs: string[],
): Promise<{ data: OrgSammenligningResponse }> {
  return api.get<OrgSammenligningResponse>(`/bruktbil/${PATH[side]}/sammenligning`, {
    params: { ...baseParams(params), org_nr: orgNrs },
    paramsSerializer: { indexes: null },
  })
}

interface RankedRow {
  org_nr: string | null
  org_name: string | null
}

/** Typeahead over the ranked list endpoint — q is name-contains / org_nr-prefix. */
export function fetchOrgOptions(
  side: OrgSide,
  params: SammenligningParams,
  q: string,
): Promise<OrgOption[]> {
  return api
    .get<{ data: RankedRow[] }>(`/bruktbil/${PATH[side]}`, {
      params: { ...baseParams(params), q, page: 1, page_size: 50 },
      paramsSerializer: { indexes: null },
    })
    .then(r =>
      r.data.data
        .filter(row => row.org_nr)
        .map(row => ({ orgNr: row.org_nr as string, navn: row.org_name })),
    )
}
